import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Loader2, CheckCircle2, CreditCard, ExternalLink } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { TREATMENTS } from "./Treatments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SQUARE_DEPOSIT_LINK = process.env.REACT_APP_SQUARE_DEPOSIT_LINK;

const TIMES = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM",
  "11:30 AM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
];

export default function BookingSection() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    treatment: "",
    preferred_time: "",
    notes: "",
  });
  const [date, setDate] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [depositConfirmed, setDepositConfirmed] = useState(false);

  // Detect return from Square (?paid=true) and mark deposit paid
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "true") {
      const storedId = sessionStorage.getItem("pendingBookingId");
      const storedName = sessionStorage.getItem("pendingBookingName");
      if (storedId) {
        axios
          .post(`${API}/bookings/${storedId}/deposit-paid`)
          .then(() => {
            setDepositConfirmed(true);
            setSuccess(true);
            setPendingBooking({ id: storedId, full_name: storedName || "" });
            toast.success("Deposit received — your appointment is confirmed!");
            sessionStorage.removeItem("pendingBookingId");
            sessionStorage.removeItem("pendingBookingName");
          })
          .catch(() => {
            toast.error("We couldn't confirm your deposit automatically. Please reach out to us.");
          });
      } else {
        // No stored booking but they came back from Square — show generic thank you
        setDepositConfirmed(true);
        setSuccess(true);
        toast.success("Thank you — your deposit has been received.");
      }
      // Clean the ?paid=true from the URL
      const url = new URL(window.location.href);
      url.searchParams.delete("paid");
      window.history.replaceState({}, "", url.pathname + url.search + "#book");
      // Scroll to booking section
      setTimeout(() => {
        document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.treatment || !date || !form.preferred_time) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      sessionStorage.setItem("pendingBookingId", data.id);
      sessionStorage.setItem("pendingBookingName", data.full_name || form.full_name);
      setPendingBooking(data);
      setSuccess(true);
      toast.success("Reservation received — one final step to confirm.");
      setForm({ full_name: "", email: "", phone: "", treatment: "", preferred_time: "", notes: "" });
      setDate(undefined);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#F5EFEB]"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="eyebrow mb-6">Book Your Appointment</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight text-[#2D2926] mb-6">
            Begin with a private
            <br />
            <em className="italic text-[#C89F95]">skin consultation</em>.
          </h2>
          <p className="text-[#5C5753] leading-relaxed font-light mb-10 max-w-md">
            Tell us a little about your skin and the moments you're working toward. We'll
            confirm your reservation within one business day with a tailored treatment proposal.
          </p>

          <div className="space-y-6 border-t border-[#E8DFD8] pt-8">
            <div>
              <p className="eyebrow text-[#C89F95] mb-2">Studio Hours</p>
              <p className="font-serif text-lg text-[#2D2926]">Tue – Sat · 9am – 5pm</p>
              <p className="text-xs text-[#5C5753] mt-1">By appointment only</p>
            </div>
            <div>
              <p className="eyebrow text-[#C89F95] mb-2">Direct Line</p>
              <a
                href="tel:0459830505"
                data-testid="booking-call-link"
                className="font-serif text-lg text-[#2D2926] hover-line"
              >
                0459 830 505
              </a>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-7 bg-[#FDFBF7] border border-[#E8DFD8] rounded-sm p-8 md:p-12"
        >
          {success ? (
            depositConfirmed ? (
              <div className="flex flex-col items-center text-center py-12" data-testid="booking-deposit-confirmed">
                <CheckCircle2 size={48} strokeWidth={1} className="text-[#C89F95] mb-6" />
                <h3 className="font-serif text-3xl text-[#2D2926] mb-4">
                  {pendingBooking?.full_name ? `Thank you, ${pendingBooking.full_name.split(" ")[0]}` : "Thank you"}
                </h3>
                <p className="eyebrow text-[#C89F95] mb-6">Appointment Confirmed</p>
                <p className="text-[#5C5753] font-light max-w-md mb-8">
                  Your $50 deposit has been received and your reservation is officially in the
                  diary. Shawnna will reach out within one business day to walk you through
                  what to expect and answer any pre-treatment questions.
                </p>
                <button
                  onClick={() => {
                    setSuccess(false);
                    setDepositConfirmed(false);
                    setPendingBooking(null);
                  }}
                  data-testid="booking-new-btn"
                  className="text-xs uppercase tracking-[0.22em] border border-[#C89F95] text-[#C89F95] hover:bg-[#C89F95] hover:text-white px-8 py-3 rounded-sm transition-colors"
                >
                  Make Another Reservation
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center text-center py-10" data-testid="booking-success">
                <CheckCircle2 size={48} strokeWidth={1} className="text-[#C89F95] mb-6" />
                <h3 className="font-serif text-3xl text-[#2D2926] mb-3">Reservation Received</h3>
                <p className="eyebrow text-[#C89F95] mb-6">One final step</p>
                <p className="text-[#5C5753] font-light max-w-md mb-8">
                  To secure your time in the diary, please complete your <strong>$50 booking
                  deposit</strong> below. Your appointment is confirmed the moment payment is
                  received — and the deposit is applied as credit toward your treatment.
                </p>

                <a
                  href={`${SQUARE_DEPOSIT_LINK}?ref=${pendingBooking?.id || ""}`}
                  data-testid="square-deposit-btn"
                  className="inline-flex items-center gap-3 bg-[#C89F95] hover:bg-[#B3867B] text-white px-8 py-4 rounded-sm text-xs uppercase tracking-[0.24em] transition-colors mb-4"
                >
                  <CreditCard size={16} strokeWidth={1.5} />
                  Pay $50 Deposit to Confirm
                  <ExternalLink size={14} strokeWidth={1.5} />
                </a>

                <p className="text-xs text-[#5C5753] font-light max-w-sm mb-8">
                  You'll be taken to a secure Square payment page. Once paid, you'll be
                  returned here automatically and your booking will be confirmed.
                </p>

                <div className="w-full max-w-sm pt-6 border-t border-[#E8DFD8] text-left">
                  <p className="eyebrow text-[#5C5753] mb-3">Deposit Policy</p>
                  <p className="text-xs text-[#5C5753] font-light leading-relaxed">
                    The $50 deposit is applied as credit toward your treatment at your
                    appointment. Non-refundable for no-shows or cancellations made less than
                    24 hours prior to your booking.
                  </p>
                </div>

                <button
                  onClick={() => setSuccess(false)}
                  className="mt-8 text-xs uppercase tracking-[0.2em] text-[#5C5753] hover:text-[#C89F95] transition-colors"
                >
                  ← Edit my booking details
                </button>
              </div>
            )
          ) : (
            <form onSubmit={onSubmit} className="space-y-7" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Full Name" required>
                  <input
                    type="text"
                    data-testid="booking-name"
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    className="bg-transparent border-b border-[#E8DFD8] focus:border-[#C89F95] py-2 outline-none w-full text-[#2D2926] transition-colors"
                    placeholder="Eloise Marquez"
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    type="tel"
                    data-testid="booking-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className="bg-transparent border-b border-[#E8DFD8] focus:border-[#C89F95] py-2 outline-none w-full text-[#2D2926] transition-colors"
                    placeholder="0400 000 000"
                  />
                </Field>
              </div>

              <Field label="Email" required>
                <input
                  type="email"
                  data-testid="booking-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="bg-transparent border-b border-[#E8DFD8] focus:border-[#C89F95] py-2 outline-none w-full text-[#2D2926] transition-colors"
                  placeholder="you@example.com"
                />
              </Field>

              <Field label="Treatment of Interest" required>
                <Select value={form.treatment} onValueChange={(v) => update("treatment", v)}>
                  <SelectTrigger
                    data-testid="booking-treatment"
                    className="bg-transparent border-x-0 border-t-0 border-b border-[#E8DFD8] rounded-none px-0 focus:ring-0 focus:border-[#C89F95] h-auto py-2 text-[#2D2926]"
                  >
                    <SelectValue placeholder="Select a treatment" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FDFBF7] border-[#E8DFD8]">
                    {TREATMENTS.map((t) => (
                      <SelectItem
                        key={t.name}
                        value={t.name}
                        data-testid={`booking-treatment-option-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {t.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="Not sure — I'd like guidance">
                      Not sure — I'd like guidance
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Preferred Date" required>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="booking-date-trigger"
                        className="bg-transparent border-b border-[#E8DFD8] focus:border-[#C89F95] py-2 outline-none w-full text-left flex items-center justify-between text-[#2D2926] transition-colors"
                      >
                        <span className={date ? "" : "text-[#5C5753]/60"}>
                          {date ? format(date, "EEEE, do MMMM") : "Choose a date"}
                        </span>
                        <CalendarIcon size={16} strokeWidth={1.25} className="text-[#C89F95]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto p-0 bg-[#FDFBF7] border-[#E8DFD8]"
                    >
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0 || d.getDay() === 1}
                        initialFocus
                        data-testid="booking-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field label="Preferred Time" required>
                  <Select
                    value={form.preferred_time}
                    onValueChange={(v) => update("preferred_time", v)}
                  >
                    <SelectTrigger
                      data-testid="booking-time"
                      className="bg-transparent border-x-0 border-t-0 border-b border-[#E8DFD8] rounded-none px-0 focus:ring-0 focus:border-[#C89F95] h-auto py-2 text-[#2D2926]"
                    >
                      <SelectValue placeholder="Choose a time" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FDFBF7] border-[#E8DFD8] max-h-[260px]">
                      {TIMES.map((tm) => (
                        <SelectItem key={tm} value={tm}>
                          {tm}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="Notes (Optional)">
                <textarea
                  data-testid="booking-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  rows={3}
                  className="bg-transparent border-b border-[#E8DFD8] focus:border-[#C89F95] py-2 outline-none w-full text-[#2D2926] resize-none transition-colors"
                  placeholder="Anything we should know about your skin or goals?"
                />
              </Field>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit-btn"
                  className="bg-[#C89F95] hover:bg-[#B3867B] disabled:opacity-60 text-white px-8 py-4 rounded-sm text-xs uppercase tracking-[0.24em] transition-colors flex items-center gap-3"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Reserving
                    </>
                  ) : (
                    "Book Now & Pay $50 Deposit"
                  )}
                </button>
                <p className="mt-4 text-xs text-[#5C5753] font-light">
                  After submitting, you'll be taken to Square to complete your $50 deposit. Your booking is confirmed the moment payment is received.
                </p>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-[#5C5753] mb-2">
        {label}
        {required && <span className="text-[#C89F95]"> *</span>}
      </span>
      {children}
    </label>
  );
}
